import { portalColors } from '../Publications/publicationModel';
import { formatCompactPrice, formatPropertyPrice } from "./propertyMap";
import { layoutPriceLabels } from "./priceLabelLayout";

// Two canvases replace hundreds of React/Google Marker instances. The base
// layer moves with the map; hover paints only one label on the top layer.
export function createPriceLayer(map, onSelect) {
  const overlay = new window.google.maps.OverlayView();
  const element = document.createElement("div");
  element.className = "property-price-layer";
  element.style.cssText = "position:absolute;pointer-events:none;";
  const base = document.createElement("canvas");
  const active = document.createElement("canvas");
  for (const canvas of [base, active]) {
    canvas.style.cssText = "position:absolute;inset:0;";
    element.appendChild(canvas);
  }
  active.style.pointerEvents = "auto";
  active.tabIndex = 0;
  active.setAttribute("role", "button");
  const keyboardHint = "Propiedades del mapa. Usá las flechas para recorrer los precios y Enter para abrir una ficha.";
  active.setAttribute("aria-label", keyboardHint);
  let records = [], source, selectedId, hoveredId = null, visited = new Set(), scene = [], hitGrid = new Map();
  let anchor = null, paintedZoom = null;
  const overscan = 160;
  let frame = 0, width = 0, height = 0, ratio = 1, origin = { x: 0, y: 0 }, destroyed = false;
  let down = null, dragged = false, keyboardIndex = -1, selectedLabel = null;
  let select = onSelect;
  const ctx = base.getContext("2d"), top = active.getContext("2d");
  const measure = document.createElement("canvas").getContext("2d");
  measure.font = "600 11px Arial, sans-serif";
  const paintLabel = (context, item, highlight = false) => {
    const { left, top: y, right, bottom } = item;
    const colors = portalColors(item.property, highlight, visited.has(item.property.id));
    if (colors.length > 1) {
      const gradient = context.createLinearGradient(left, 0, right, 0);
      colors.forEach((color, i) => { gradient.addColorStop(i / colors.length, color); gradient.addColorStop((i + 1) / colors.length, color); });
      context.fillStyle = gradient;
    } else context.fillStyle = colors[0];
    context.beginPath();
    const r = 6, w = right - left, h = bottom - y;
    context.moveTo(left + r, y);
    context.arcTo(right, y, right, bottom, r);
    context.arcTo(right, bottom, left, bottom, r);
    context.arcTo(left, bottom, left, y, r);
    context.arcTo(left, y, right, y, r);
    context.closePath();
    context.shadowColor = "rgba(75,35,12,.18)"; context.shadowBlur = 0; context.shadowOffsetY = 1;
    context.fill();
    context.shadowBlur = 0; context.shadowOffsetY = 0;
    // Text is painted together with its opaque background. Overlapping prices
    // cannot mix glyphs from different markers as separate Marker labels do.
    context.fillStyle = "#ffffff";
    context.font = "600 11px Arial, sans-serif";
    context.textAlign = "center"; context.textBaseline = "middle";
    context.fillText(item.text, left + w / 2, y + h / 2 + .5);
  };
  const paintDot = (context, item, highlight = false) => {
    context.beginPath();
    context.arc(item.x, item.y, highlight ? 4.5 : 3.5, 0, Math.PI * 2);
    context.fillStyle = portalColors(item.property, highlight)[0];
    context.fill();
  };
  const paintActive = () => {
    top.clearRect(0, 0, width, height);
    if (selectedLabel) { paintLabel(top, selectedLabel, true); paintDot(top, selectedLabel, true); }
    const hovered = scene.find((item) => item.property.id === hoveredId);
    if (hovered && hovered !== selectedLabel) { paintLabel(top, hovered, true); paintDot(top, hovered, true); }
  };
  const setHover = (item) => {
    const id = item?.property.id ?? null;
    if (hoveredId === id) return;
    hoveredId = id;
    active.style.cursor = item ? "pointer" : "grab";
    active.title = item?.title || "";
    active.setAttribute("aria-label", item ? `${item.title}. Enter para abrir.` : keyboardHint);
    paintActive();
  };
  const hit = (x, y) => {
    const inside = (item) => item && ((x >= item.left && x <= item.right && y >= item.top && y <= item.bottom) || Math.hypot(x - item.x, y - item.y) <= 7);
    const hovered = scene.find((item) => item.property.id === hoveredId);
    if (inside(hovered)) return hovered;
    if (inside(selectedLabel)) return selectedLabel;
    const candidates = hitGrid.get(`${Math.floor(x / 96)},${Math.floor(y / 96)}`) || [];
    for (let i = candidates.length - 1; i >= 0; i--) if (inside(candidates[i])) return candidates[i];
    return null;
  };
  const position = (event) => {
    const rect = active.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };
  active.addEventListener("pointerdown", (event) => { down = { x: event.clientX, y: event.clientY }; dragged = false; });
  active.addEventListener("pointermove", (event) => {
    if (down && Math.hypot(event.clientX - down.x, event.clientY - down.y) > 5) dragged = true;
    if (event.buttons || dragged) return;
    const p = position(event); setHover(hit(p.x, p.y));
  });
  active.addEventListener("pointerleave", () => { down = null; setHover(null); });
  active.addEventListener("pointerup", () => { down = null; });
  active.addEventListener("click", (event) => {
    if (dragged) { dragged = false; return; }
    const p = position(event), item = hit(p.x, p.y);
    if (item) { event.stopPropagation(); select(item.property); }
  });
  active.addEventListener("keydown", (event) => {
    if (!scene.length) return;
    if (["ArrowRight", "ArrowDown", "ArrowLeft", "ArrowUp"].includes(event.key)) {
      event.preventDefault();
      const step = ["ArrowLeft", "ArrowUp"].includes(event.key) ? -1 : 1;
      keyboardIndex = (keyboardIndex + step + scene.length) % scene.length;
      setHover(scene[keyboardIndex]);
    } else if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      const item = scene.find((p) => p.property.id === hoveredId) || scene[0];
      select(item.property);
    } else if (event.key === "Escape") setHover(null);
  });
  active.addEventListener("blur", () => setHover(null));
  const render = () => {
    frame = 0;
    if (destroyed) return;
    const projection = overlay.getProjection(), center = map.getCenter();
    if (!projection || !center) return;
    const div = map.getDiv();
    const w = div.clientWidth + overscan * 2, h = div.clientHeight + overscan * 2, dpr = window.devicePixelRatio || 1;
    if (!div.clientWidth || !div.clientHeight) return;
    if (w !== width || h !== height || dpr !== ratio) {
      width = w; height = h; ratio = dpr;
      element.style.width = `${w}px`; element.style.height = `${h}px`;
      for (const canvas of [base, active]) {
        canvas.width = Math.round(w * dpr); canvas.height = Math.round(h * dpr);
        canvas.style.width = `${w}px`; canvas.style.height = `${h}px`;
        canvas.getContext("2d").setTransform(dpr, 0, 0, dpr, 0, 0);
      }
    }
    const c = projection.fromLatLngToDivPixel(center);
    origin = { x: c.x - w / 2, y: c.y - h / 2 };
    element.style.left = `${origin.x}px`; element.style.top = `${origin.y}px`;
    anchor = projection.fromDivPixelToLatLng(new window.google.maps.Point(origin.x, origin.y));
    paintedZoom = map.getZoom();
    const visible = [];
    for (const record of records) {
      const p = projection.fromLatLngToDivPixel(record.position);
      const x = p.x - origin.x, y = p.y - origin.y;
      if (x < -60 || x > w + 60 || y < -30 || y > h + 30) continue;
      visible.push({ ...record, x, y });
    }
    scene = layoutPriceLabels(visible, w, h);
    ctx.clearRect(0, 0, w, h);
    hitGrid = new Map(); selectedLabel = null;
    // Small leaders preserve the real location when labels need extra spacing.
    ctx.strokeStyle = "rgba(169,67,19,.32)"; ctx.lineWidth = 1;
    ctx.beginPath();
    for (const item of scene) {
      const cx = (item.left + item.right) / 2, cy = (item.top + item.bottom) / 2;
      if (Math.abs(cx - item.x) > 4 || Math.abs(cy - item.y) > 4) { ctx.moveTo(item.x, item.y); ctx.lineTo(cx, cy); }
    }
    ctx.stroke();
    for (const item of scene) paintDot(ctx, item);
    for (const item of scene) {
      paintLabel(ctx, item);
      if (item.property.id === selectedId) selectedLabel = item;
      for (let x = Math.floor(Math.min(item.left, item.x - 7) / 96); x <= Math.floor(Math.max(item.right, item.x + 7) / 96); x++) {
        for (let y = Math.floor(Math.min(item.top, item.y - 7) / 96); y <= Math.floor(Math.max(item.bottom, item.y + 7) / 96); y++) {
          const key = `${x},${y}`;
          if (!hitGrid.has(key)) hitGrid.set(key, []);
          hitGrid.get(key).push(item);
        }
      }
    }
    paintActive();
  };
  const schedule = () => { if (!destroyed && !frame) frame = requestAnimationFrame(render); };
  overlay.onAdd = () => { overlay.getPanes().overlayMouseTarget.appendChild(element); schedule(); };
  overlay.draw = () => {
    // Panning translates the cached drawing. Recompute layout when the map
    // settles, not on every animation frame. Overscan covers newly exposed edges.
    const projection = overlay.getProjection();
    if (anchor && paintedZoom === map.getZoom() && projection) {
      const point = projection.fromLatLngToDivPixel(anchor);
      element.style.left = `${point.x}px`; element.style.top = `${point.y}px`;
    } else schedule();
  };
  overlay.onRemove = () => element.remove();
  overlay.setMap(map);
  const resize = new ResizeObserver(schedule); resize.observe(map.getDiv());
  const idle = map.addListener("idle", schedule);
  return {
    update(properties, selection, visitedIds, callback) {
      select = callback;
      let changed = source !== properties;
      if (changed) {
        source = properties;
        const previous = new Map(records.map((record) => [record.property.id, record]));
        records = properties.filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng)).map((property) => {
          const cached = previous.get(property.id);
          if (cached?.property === property) return cached;
          const text = formatCompactPrice(property.price, property.price_currency);
          return { property, text, width: Math.ceil(measure.measureText(text).width) + 14,
            title: `${formatPropertyPrice(property.price, property.price_currency)} · ${property.address || "Ver propiedad"}`,
            position: new window.google.maps.LatLng(property.lat, property.lng) };
        });
        hoveredId = null;
      }
      const nextVisited = new Set(visitedIds);
      if (nextVisited.size !== visited.size || [...nextVisited].some((id) => !visited.has(id))) changed = true;
      visited = nextVisited; selectedId = selection;
      if (changed || !scene.length) schedule();
      else { selectedLabel = scene.find((item) => item.property.id === selectedId) || null; paintActive(); }
    },
    destroy() {
      destroyed = true; cancelAnimationFrame(frame); resize.disconnect(); idle.remove(); overlay.setMap(null);
    },
  };
}
